import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";

const TOL = 1e-9;

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function numberLatex(value, { mixed = false } = {}) {
  if (value === Infinity) return "\\infty";
  if (value === -Infinity) return "-\\infty";
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < TOL) return String(rounded);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(abs * d);
    if (Math.abs(abs - n / d) < TOL) {
      const g = gcd(n, d);
      const numerator = n / g;
      const denominator = d / g;
      if (mixed && numerator > denominator) {
        const whole = Math.floor(numerator / denominator);
        const rest = numerator % denominator;
        return rest === 0
          ? `${sign}${whole}`
          : `${sign}${whole}\\dfrac{${rest}}{${denominator}}`;
      }
      return `${sign}\\dfrac{${numerator}}{${denominator}}`;
    }
  }
  return String(Number(value.toFixed(3))).replace(".", ",");
}

function denominatorOf(value) {
  if (!Number.isFinite(value)) return 1;
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < TOL) return 1;
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(value * d);
    if (Math.abs(value - n / d) < TOL) return d / gcd(n, d);
  }
  return Infinity;
}

function endpointLooksOk(value) {
  return !Number.isFinite(value) || Math.abs(value) <= 20;
}

function intervalLooksOk(interval) {
  return denominatorOf(interval.left) <= 6
    && denominatorOf(interval.right) <= 6
    && endpointLooksOk(interval.left)
    && endpointLooksOk(interval.right);
}
function compactLinearLatex(a, b) {
  const parts = [];
  if (a !== 0) {
    if (a === 1) parts.push("x");
    else if (a === -1) parts.push("-x");
    else parts.push(`${a}x`);
  }
  if (b !== 0 || parts.length === 0) {
    const sign = b > 0 && parts.length ? "+" : "";
    parts.push(`${sign}${b}`);
  }
  return parts.join("");
}

function signedXTermLatex(coefficient) {
  if (coefficient === 1) return "+x";
  if (coefficient === -1) return "-x";
  return coefficient > 0 ? `+${coefficient}x` : `${coefficient}x`;
}

function numberRaw(value) {
  if (!Number.isFinite(value)) return String(value);
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < TOL) return String(rounded);
  for (let d = 2; d <= 24; d += 1) {
    const n = Math.round(value * d);
    if (Math.abs(value - n / d) < TOL) {
      const g = gcd(n, d);
      return `${n / g}/${d / g}`;
    }
  }
  return String(Number(value.toFixed(4))).replace(".", ",");
}
function termForRoot(root, increasing, scale = 1) {
  const a = increasing ? scale : -scale;
  const b = -a * root;
  return { a, b, root, latex: compactLinearLatex(a, b) };
}

function sumPartsLatex(parts) {
  const tokens = [];
  parts.forEach((part) => {
    if (part.type === "x") {
      if (part.value === 0) return;
      const abs = Math.abs(part.value);
      tokens.push({ sign: part.value < 0 ? "-" : "+", body: abs === 1 ? "x" : `${abs}x` });
      return;
    }
    if (part.value === 0) return;
    tokens.push({ sign: part.value < 0 ? "-" : "+", body: String(Math.abs(part.value)) });
  });
  if (!tokens.length) return "0";
  return tokens.map((token, index) => index === 0
    ? (token.sign === "-" ? `-${token.body}` : token.body)
    : `${token.sign}${token.body}`
  ).join("");
}

function expandedLinearSumLatex(items) {
  const parts = [];
  items.forEach(({ term, sign, coefficient = 1 }) => {
    parts.push({ type: "x", value: coefficient * sign * term.a });
    parts.push({ type: "const", value: coefficient * sign * term.b });
  });
  return sumPartsLatex(parts);
}

const WEIGHTED_ABS_COEFFICIENTS = [-5, -4, -3, -2, 2, 3, 4, 5];

function scaledParenthesizedLatex(multiplier, latex, index = 0) {
  const abs = Math.abs(multiplier);
  const body = `${abs === 1 ? "" : abs}(${latex})`;
  if (index === 0) return multiplier < 0 ? `-${body}` : body;
  return `${multiplier < 0 ? "-" : "+"}${body}`;
}

function scaledAbsoluteLatex(coefficient, latex, index = 0) {
  const abs = Math.abs(coefficient);
  const body = `${abs === 1 ? "" : abs}\\left|${latex}\\right|`;
  if (index === 0) return coefficient < 0 ? `-${body}` : body;
  return `${coefficient < 0 ? "-" : "+"}${body}`;
}

function pickRelation(direction) {
  const less = ["<", "\\le"];
  const greater = [">", "\\ge"];
  const pool = direction === "less" ? less : direction === "greater" ? greater : (Math.random() < 0.5 ? less : greater);
  return pool[Math.floor(Math.random() * pool.length)];
}
function relationHolds(value, relation) {
  if (relation === "<") return value < -TOL;
  if (relation === "\\le") return value <= TOL;
  if (relation === ">") return value > TOL;
  return value >= -TOL;
}

function intervalLabel(interval) {
  if (interval.left === -Infinity) return `x${interval.rightClosed ? "\\le{}" : "<"}${numberLatex(interval.right, { mixed: true })}`;
  if (interval.right === Infinity) return `x${interval.leftClosed ? "\\ge{}" : ">"}${numberLatex(interval.left, { mixed: true })}`;
  return `${numberLatex(interval.left, { mixed: true })}${interval.leftClosed ? "\\le{}" : "<"}x${interval.rightClosed ? "\\le{}" : "<"}${numberLatex(interval.right, { mixed: true })}`;
}

function intervalLatex(interval) {
  if (Math.abs(interval.left - interval.right) < TOL && interval.leftClosed && interval.rightClosed) {
    return `\\{${numberLatex(interval.left, { mixed: true })}\\}`;
  }
  return `${interval.leftClosed ? "[" : "("}${numberLatex(interval.left, { mixed: true })};${numberLatex(interval.right, { mixed: true })}${interval.rightClosed ? "]" : ")"}`;
}

function intervalsLatex(intervals) {
  if (!intervals.length) return "\\varnothing";
  if (intervals.length === 1 && intervals[0].left === -Infinity && intervals[0].right === Infinity) return "\\mathbb{R}";
  return intervals.map(intervalLatex).join("\\cup");
}

function isAllRealsAnswer(intervals) {
  return intervals.length === 1
    && intervals[0].left === -Infinity
    && intervals[0].right === Infinity;
}
function intersectIntervals(a, b) {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  let leftClosed = a.left > b.left ? a.leftClosed : b.left > a.left ? b.leftClosed : a.leftClosed && b.leftClosed;
  let rightClosed = a.right < b.right ? a.rightClosed : b.right < a.right ? b.rightClosed : a.rightClosed && b.rightClosed;
  if (left > right + TOL) return null;
  if (Math.abs(left - right) < TOL) {
    if (!leftClosed || !rightClosed) return null;
    leftClosed = true;
    rightClosed = true;
  }
  return { left, right, leftClosed, rightClosed };
}

function solutionForLinearInequality(coef, constant, relation) {
  if (coef === 0) {
    return relationHolds(constant, relation)
      ? [{ left: -Infinity, right: Infinity, leftClosed: false, rightClosed: false }]
      : [];
  }
  const root = -constant / coef;
  const greaterSide = coef > 0;
  const wantGreater = relation === ">" || relation === "\\ge";
  const closed = relation === "\\le" || relation === "\\ge";
  const chooseRight = greaterSide === wantGreater;
  return chooseRight
    ? [{ left: root, right: Infinity, leftClosed: closed, rightClosed: false }]
    : [{ left: -Infinity, right: root, leftClosed: false, rightClosed: closed }];
}

function normalizeIntervals(intervals) {
  const sorted = intervals.filter(Boolean).sort((a, b) => a.left - b.left || Number(b.leftClosed) - Number(a.leftClosed));
  const out = [];
  sorted.forEach((item) => {
    const last = out[out.length - 1];
    if (!last) {
      out.push({ ...item });
      return;
    }
    const touches = Math.abs(last.right - item.left) < TOL;
    const overlaps = item.left < last.right - TOL || (touches && (last.rightClosed || item.leftClosed));
    if (!overlaps) {
      out.push({ ...item });
      return;
    }
    if (item.right > last.right + TOL) {
      last.right = item.right;
      last.rightClosed = item.rightClosed;
    } else if (Math.abs(item.right - last.right) < TOL) {
      last.rightClosed = last.rightClosed || item.rightClosed;
    }
  });
  return out;
}

function sameIntervals(expected, parsed) {
  if (!parsed) return false;
  if (!expected.length) return parsed.kind === "empty" || (parsed.kind === "intervals" && parsed.intervals.length === 0);
  if (expected.length === 1 && expected[0].left === -Infinity && expected[0].right === Infinity) {
    return parsed.kind === "all" || (parsed.kind === "intervals" && parsed.intervals.length === 1 && parsed.intervals[0].left === -Infinity && parsed.intervals[0].right === Infinity);
  }
  const got = parsed.kind === "intervals"
    ? parsed.intervals
    : parsed.kind === "point"
      ? [{ left: parsed.value, right: parsed.value, leftClosed: true, rightClosed: true }]
      : parsed.kind === "finite"
        ? parsed.points.map((value) => ({ left: value, right: value, leftClosed: true, rightClosed: true }))
        : [];
  const normGot = normalizeIntervals(got);
  if (normGot.length !== expected.length) return false;
  return expected.every((exp, index) => {
    const g = normGot[index];
    return sameEndpoint(exp.left, g.left) && sameEndpoint(exp.right, g.right) && !!exp.leftClosed === !!g.leftClosed && !!exp.rightClosed === !!g.rightClosed;
  });
}


function sameEndpoint(a, b) {
  if (a === b) return true;
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < TOL;
}
function criticalIntervals(points) {
  const roots = Array.from(new Set(points.filter(Number.isFinite).map((x) => Number(x.toFixed(10))))).sort((a, b) => a - b);
  const intervals = [];
  intervals.push({ left: -Infinity, right: roots[0], leftClosed: false, rightClosed: false, probe: roots[0] - 1 });
  for (let i = 0; i < roots.length - 1; i += 1) {
    intervals.push({ left: roots[i], right: roots[i + 1], leftClosed: true, rightClosed: false, probe: (roots[i] + roots[i + 1]) / 2 });
  }
  intervals.push({ left: roots[roots.length - 1], right: Infinity, leftClosed: true, rightClosed: false, probe: roots[roots.length - 1] + 1 });
  return intervals;
}

function solveBySampling(boundaries, evalFn, relation) {
  const intervals = criticalIntervals(boundaries);
  const pieces = [];
  intervals.forEach((interval) => {
    if (relationHolds(evalFn(interval.probe), relation)) pieces.push({ left: interval.left, right: interval.right, leftClosed: false, rightClosed: false });
  });
  boundaries.forEach((point) => {
    if (relationHolds(evalFn(point), relation)) pieces.push({ left: point, right: point, leftClosed: true, rightClosed: true });
  });
  return normalizeIntervals(pieces);
}

function nestedInequalityLines({ linearLatex, center, delta, relation }) {
  const high = center + delta;
  const low = center - delta;
  const isUpper = relation === "<" || relation === "\\le";
  const weak = relation === "\\le" || relation === "\\ge";
  if (isUpper) {
    const leftRel = weak ? "\\le" : "<";
    const rightRel = relation;
    const outer = `-${delta}\\;${leftRel}\\;\\left|${linearLatex}\\right|-${center}\\;${rightRel}\\;${delta}`;
    const shifted = `${numberLatex(low)}\\;${leftRel}\\;\\left|${linearLatex}\\right|\\;${rightRel}\\;${numberLatex(high)}`;
    const lowerLinear = rightRel === "<" ? "<" : "\\le";
    const linear = low > 0
      ? `-${numberLatex(high)}\\;${lowerLinear}\\;${linearLatex}\\;${rightRel}\\;- ${numberLatex(low)}\\quad\\text{lub}\\quad ${numberLatex(low)}\\;${leftRel}\\;${linearLatex}\\;${rightRel}\\;${numberLatex(high)}`
      : `-${numberLatex(high)}\\;${lowerLinear}\\;${linearLatex}\\;${rightRel}\\;${numberLatex(high)}`;
    return { outer, shifted, linear };
  }
  const lowRel = weak ? "\\le" : "<";
  const highRel = weak ? "\\ge" : ">";
  const outer = `\\left|${linearLatex}\\right|-${center}\\;${lowRel}\\;-${delta}\\quad\\text{lub}\\quad \\left|${linearLatex}\\right|-${center}\\;${relation}\\;${delta}`;
  const shifted = low >= 0
    ? `\\left|${linearLatex}\\right|\\;${lowRel}\\;${numberLatex(low)}\\quad\\text{lub}\\quad \\left|${linearLatex}\\right|\\;${highRel}\\;${numberLatex(high)}`
    : `\\left|${linearLatex}\\right|\\;${highRel}\\;${numberLatex(high)}`;
  const first = low >= 0
    ? `-${numberLatex(low)}\\;${weak ? "\\le" : "<"}\\;${linearLatex}\\;${lowRel}\\;${numberLatex(low)}`
    : "";
  const second = `${linearLatex}\\;${relation}\\;${numberLatex(high)}\\quad\\text{lub}\\quad ${linearLatex}\\;${relation === ">" ? "<" : "\\le"}\\;-${numberLatex(high)}`;
  return { outer, shifted, linear: first ? `${first}\\quad\\text{lub}\\quad ${second}` : second };
}

function buildNestedStaticFallback(relationDirection) {
  const relation = relationDirection === "greater" ? "\\ge" : "\\le";
  const center = 3;
  const delta = 2;
  const linearLatex = "x";
  const answer = relationDirection === "greater"
    ? [
        { left: -Infinity, right: -5, leftClosed: false, rightClosed: true },
        { left: -1, right: 1, leftClosed: true, rightClosed: true },
        { left: 5, right: Infinity, leftClosed: true, rightClosed: false },
      ]
    : [
        { left: -5, right: -1, leftClosed: true, rightClosed: true },
        { left: 1, right: 5, leftClosed: true, rightClosed: true },
      ];
  return {
    type: "AdvancedAbsoluteInequalityTask",
    data: {
      id: `nested-ineq-fallback-${relation}`,
      family: "nested",
      linearLatex,
      center,
      delta,
      relation,
      nested: nestedInequalityLines({ linearLatex, center, delta, relation }),
      equation: `\\left|\\left|${linearLatex}\\right|-${center}\\right|\\;${relation}\\;${delta}`,
      critical: [-3, 0, 3],
      splitLatex: `|${linearLatex}|-${center}`,
      intervals: [],
      answer,
      steps: [],
    },
    answer,
    maxPoints: 1,
  };
}
function buildNestedTask({ linear = false, relationDirection, fallback = false } = {}) {
  for (let attempt = 0; attempt < 300; attempt += 1) {
    const a = linear ? [1, 2, 3][Math.floor(Math.random() * 3)] : 1;
    const b = linear ? -5 + Math.floor(Math.random() * 11) : 0;
    const center = 2 + Math.floor(Math.random() * 7);
    const delta = 1 + Math.floor(Math.random() * Math.min(5, center + 2));
    const relation = pickRelation(relationDirection);
    const linearLatex = compactLinearLatex(a, b);
    const evalFn = (x) => Math.abs(Math.abs(a * x + b) - center) - delta;
    const boundaryValues = [center + delta];
    if (center - delta >= 0) boundaryValues.push(center - delta);
    const boundaries = boundaryValues.flatMap((value) => [(value - b) / a, (-value - b) / a]).filter(Number.isFinite);
    const answer = solveBySampling(boundaries, evalFn, relation);
    if (!answer.length || answer.length > 4 || isAllRealsAnswer(answer)) continue;
    const nice = answer.every(intervalLooksOk);
    if (!nice) continue;
    const casePoints = [(-center - b) / a, -b / a, (center - b) / a].filter(Number.isFinite);
    const intervals = criticalIntervals(casePoints);
    const nested = nestedInequalityLines({ linearLatex, center, delta, relation });
    const caseSteps = intervals.map((interval) => {
      const L = a * interval.probe + b;
      const innerSign = L >= 0 ? 1 : -1;
      const innerCoef = innerSign * a;
      const innerConst = innerSign * b;
      const outerInsideConst = innerConst - center;
      const outerInside = compactLinearLatex(innerCoef, outerInsideConst);
      const outerSign = Math.abs(L) - center >= 0 ? 1 : -1;
      const outerCoef = outerSign * innerCoef;
      const outerConst = outerSign * outerInsideConst;
      const outerExpanded = compactLinearLatex(outerCoef, outerConst);
      const diffLatex = compactLinearLatex(outerCoef, outerConst - delta);
      const raw = solutionForLinearInequality(outerCoef, outerConst - delta, relation);
      const local = normalizeIntervals(raw.map((candidate) => intersectIntervals(interval, candidate)).filter(Boolean));
      const innerStep = innerSign > 0 ? `(${linearLatex})` : `-(${linearLatex})`;
      const outerStep = outerSign > 0 ? `(${outerInside})` : `-(${outerInside})`;
      return `${intervalLabel(interval)}:\\quad \\left|\\left|${linearLatex}\\right|-${center}\\right|\\;${relation}\\;${delta}`
        + `\\Rightarrow \\left|${innerStep}-${center}\\right|\\;${relation}\\;${delta}`
        + `\\Rightarrow \\left|${outerInside}\\right|\\;${relation}\\;${delta}`
        + `\\Rightarrow ${outerStep}\\;${relation}\\;${delta}`
        + `\\Rightarrow ${outerExpanded}\\;${relation}\\;${delta}`
        + `\\Rightarrow ${diffLatex}\\;${relation}\\;0`
        + `\\Rightarrow x\\in ${intervalsLatex(local)}`;
    });
    return {
      type: "AdvancedAbsoluteInequalityTask",
      data: {
        id: `nested-ineq-${linear ? "linear" : "simple"}-${a}-${b}-${center}-${delta}-${relation}`,
        family: "nested",
        linearLatex,
        center,
        delta,
        relation,
        nested,
        equation: `\\left|\\left|${linearLatex}\\right|-${center}\\right|\\;${relation}\\;${delta}`,
        critical: casePoints,
        splitLatex: `|${linearLatex}|-${center}`,
        intervals: intervals.map(intervalLabel),
        answer,
        steps: [
          `Punkty krytyczne: x=${casePoints.map(numberLatex).join(",\\;")}`,
          ...caseSteps,
        ],
      },
      answer,
      maxPoints: 1,
    };
  }
  if (!fallback) return buildNestedTask({ linear: false, relationDirection, fallback: true });
  return buildNestedStaticFallback(relationDirection);
}


function buildDoubleStaticFallback(
  relationDirection,
  { withLinearRight = false, weighted = false } = {},
) {
  const relation = relationDirection === "greater" ? "\\ge" : "\\le";
  const leftTerm = termForRoot(-2, true, 1);
  const rightTerm = termForRoot(3, true, 1);
  const absoluteCoefficients = weighted ? [2, -3] : [1, 1];
  const rhsCoef = withLinearRight ? -1 : 0;
  const rhsConst = weighted ? 5 : 7;
  const rhsLatex = compactLinearLatex(rhsCoef, rhsConst);
  const baseIntervals = [
    { left: -Infinity, right: -2, leftClosed: false, rightClosed: false, probe: -3 },
    { left: -2, right: 3, leftClosed: true, rightClosed: false, probe: 0 },
    { left: 3, right: Infinity, leftClosed: true, rightClosed: false, probe: 4 },
  ];
  const pieces = [];
  const caseSteps = baseIntervals.map((base) => {
    const signedTerms = [leftTerm, rightTerm].map((term, index) => ({
      term,
      sign: term.a * base.probe + term.b >= 0 ? 1 : -1,
      coefficient: absoluteCoefficients[index],
    }));
    const substituted = signedTerms
      .map(({ term, sign, coefficient }, index) => (
        scaledParenthesizedLatex(coefficient * sign, term.latex, index)
      ))
      .join("");
    const expanded = expandedLinearSumLatex(signedTerms);
    const leftCoef = signedTerms.reduce(
      (sum, item) => sum + item.coefficient * item.sign * item.term.a,
      0,
    );
    const leftConst = signedTerms.reduce(
      (sum, item) => sum + item.coefficient * item.sign * item.term.b,
      0,
    );
    const diffCoef = leftCoef - rhsCoef;
    const diffConst = leftConst - rhsConst;
    const raw = solutionForLinearInequality(diffCoef, diffConst, relation);
    const local = normalizeIntervals(raw.map((interval) => intersectIntervals(base, interval)).filter(Boolean));
    pieces.push(...local);
    const reduced = compactLinearLatex(leftCoef, leftConst);
    const diffLatex = compactLinearLatex(diffCoef, diffConst);
    return `${intervalLabel(base)}:\\quad ${substituted}\\;${relation}\\;${rhsLatex}\\Rightarrow ${expanded}\\;${relation}\\;${rhsLatex}\\Rightarrow ${reduced}\\;${relation}\\;${rhsLatex}\\Rightarrow ${diffLatex}\\;${relation}\\;0\\Rightarrow x\\in ${intervalsLatex(local)}`;
  });
  const answer = normalizeIntervals(pieces);
  return {
    type: "AdvancedAbsoluteInequalityTask",
    data: {
      id: `double-ineq-fallback-${weighted ? "weighted" : "plain"}-${withLinearRight ? "linear" : "const"}-${relation}`,
      family: "double",
      weighted,
      absoluteCoefficients,
      outsideLinearCoefficient: rhsCoef,
      equation: `${scaledAbsoluteLatex(absoluteCoefficients[0], leftTerm.latex)}${scaledAbsoluteLatex(absoluteCoefficients[1], rightTerm.latex, 1)}\\;${relation}\\;${rhsLatex}`,
      critical: [-2, 3],
      splitLatex: `${leftTerm.latex},\\;${rightTerm.latex}`,
      intervals: baseIntervals.map(intervalLabel),
      answer,
      steps: [
        "Punkty krytyczne: x=-2\\;\\text{oraz}\\;x=3",
        ...caseSteps,
      ],
    },
    answer,
    maxPoints: 1,
  };
}

function buildSingleLinearTask({ relationDirection, weighted = false, fallback = false } = {}) {
  const buildCandidate = ({
    root,
    increasing,
    scale,
    absCoefficient,
    outsideCoef,
    rhsConst,
    relation,
  }) => {
    const absoluteTerm = termForRoot(root, increasing, scale);
    const outsideLatex = signedXTermLatex(outsideCoef);
    const rhsLatex = numberLatex(rhsConst);
    const baseIntervals = [
      { left: -Infinity, right: root, leftClosed: false, rightClosed: false, probe: root - 1 },
      { left: root, right: Infinity, leftClosed: true, rightClosed: false, probe: root + 1 },
    ];
    const pieces = [];
    const caseSteps = [];

    baseIntervals.forEach((base) => {
      const sign = absoluteTerm.a * base.probe + absoluteTerm.b >= 0 ? 1 : -1;
      const substituted = `${scaledParenthesizedLatex(absCoefficient * sign, absoluteTerm.latex)}${outsideLatex}`;
      const leftCoef = absCoefficient * sign * absoluteTerm.a + outsideCoef;
      const leftConst = absCoefficient * sign * absoluteTerm.b;
      const expanded = sumPartsLatex([
        { type: "x", value: absCoefficient * sign * absoluteTerm.a },
        { type: "const", value: absCoefficient * sign * absoluteTerm.b },
        { type: "x", value: outsideCoef },
      ]);
      const diffCoef = leftCoef;
      const diffConst = leftConst - rhsConst;
      const raw = solutionForLinearInequality(diffCoef, diffConst, relation);
      const local = normalizeIntervals(raw
        .map((interval) => intersectIntervals(base, interval))
        .filter(Boolean));
      pieces.push(...local);

      const reduced = compactLinearLatex(leftCoef, leftConst);
      const diffLatex = compactLinearLatex(diffCoef, diffConst);
      caseSteps.push(
        `${intervalLabel(base)}:\\quad ${substituted}\\;${relation}\\;${rhsLatex}`
        + `\\Rightarrow ${expanded}\\;${relation}\\;${rhsLatex}`
        + `\\Rightarrow ${reduced}\\;${relation}\\;${rhsLatex}`
        + `\\Rightarrow ${diffLatex}\\;${relation}\\;0`
        + `\\Rightarrow x\\in ${intervalsLatex(local)}`,
      );
    });

    const answer = normalizeIntervals(pieces);
    if (!answer.length || answer.length > 3 || isAllRealsAnswer(answer)) return null;
    if (!answer.every(intervalLooksOk)) return null;

    return {
      type: "AdvancedAbsoluteInequalityTask",
      data: {
        id: `single-linear-ineq-${weighted ? "weighted" : "plain"}-${root}-${scale}-${absCoefficient}-${outsideCoef}-${relation}-${rhsConst}`,
        family: "single-linear",
        weighted,
        absoluteCoefficients: [absCoefficient],
        outsideLinearCoefficient: outsideCoef,
        equation: `${scaledAbsoluteLatex(absCoefficient, absoluteTerm.latex)}${outsideLatex}\\;${relation}\\;${rhsLatex}`,
        critical: [root],
        splitLatex: absoluteTerm.latex,
        intervals: baseIntervals.map(intervalLabel),
        answer,
        steps: [
          `Punkt krytyczny: x=${numberLatex(root)}`,
          ...caseSteps,
        ],
      },
      answer,
      maxPoints: 1,
    };
  };

  if (fallback) {
    return buildCandidate({
      root: 2,
      increasing: true,
      scale: 1,
      absCoefficient: weighted ? -2 : 1,
      outsideCoef: 1,
      rhsConst: 6,
      relation: relationDirection === "greater" ? "\\ge" : "\\le",
    });
  }

  for (let attempt = 0; attempt < 2000; attempt += 1) {
    const candidate = buildCandidate({
      root: -4 + Math.floor(Math.random() * 9),
      increasing: Math.random() < 0.5,
      scale: Math.random() < 0.7 ? 1 : 2,
      absCoefficient: weighted
        ? WEIGHTED_ABS_COEFFICIENTS[Math.floor(Math.random() * WEIGHTED_ABS_COEFFICIENTS.length)]
        : 1,
      outsideCoef: [-3, -2, -1, 1, 2, 3][Math.floor(Math.random() * 6)],
      rhsConst: -7 + Math.floor(Math.random() * 18),
      relation: pickRelation(relationDirection),
    });
    if (candidate) return candidate;
  }

  return buildSingleLinearTask({ relationDirection, weighted, fallback: true });
}

function buildDoubleTask({
  withLinearRight = false,
  scaled = false,
  weighted = false,
  relationDirection,
  fallback = false,
} = {}) {
  const orientations = [[true, true], [true, false], [false, true], [false, false]];
  for (let attempt = 0; attempt < 500; attempt += 1) {
    const leftRoot = -6 + Math.floor(Math.random() * 9);
    const rightRoot = leftRoot + 2 + Math.floor(Math.random() * Math.min(7, 6 - leftRoot));
    const [leftInc, rightInc] = orientations[Math.floor(Math.random() * orientations.length)];
    const leftScale = scaled ? [1, 2][Math.floor(Math.random() * 2)] : 1;
    const rightScale = scaled ? [1, 2][Math.floor(Math.random() * 2)] : 1;
    const leftTerm = termForRoot(leftRoot, leftInc, leftScale);
    const rightTerm = termForRoot(rightRoot, rightInc, rightScale);
    const absoluteCoefficients = weighted
      ? (Math.random() < 0.5
        ? [2 + Math.floor(Math.random() * 4), -(2 + Math.floor(Math.random() * 4))]
        : [-(2 + Math.floor(Math.random() * 4)), 2 + Math.floor(Math.random() * 4)])
      : [1, 1];
    const relation = pickRelation(relationDirection);
    const rhsCoef = withLinearRight ? [-3, -2, -1, 1, 2, 3][Math.floor(Math.random() * 6)] : 0;
    const rhsConst = withLinearRight ? -7 + Math.floor(Math.random() * 15) : (rightRoot - leftRoot) + [-2, -1, 0, 1, 2, 3][Math.floor(Math.random() * 6)];
    const rhsLatex = compactLinearLatex(rhsCoef, rhsConst);
    const baseIntervals = [
      { left: -Infinity, right: leftRoot, leftClosed: false, rightClosed: false, probe: leftRoot - 1 },
      { left: leftRoot, right: rightRoot, leftClosed: true, rightClosed: false, probe: (leftRoot + rightRoot) / 2 },
      { left: rightRoot, right: Infinity, leftClosed: true, rightClosed: false, probe: rightRoot + 1 },
    ];
    const pieces = [];
    const caseSteps = [];
    baseIntervals.forEach((base) => {
      const signedTerms = [leftTerm, rightTerm].map((term, index) => ({
        term,
        sign: term.a * base.probe + term.b >= 0 ? 1 : -1,
        coefficient: absoluteCoefficients[index],
      }));
      const substituted = signedTerms
        .map(({ term, sign, coefficient }, index) => scaledParenthesizedLatex(coefficient * sign, term.latex, index))
        .join("");
      const expanded = expandedLinearSumLatex(signedTerms);
      const leftCoef = signedTerms.reduce((sum, item) => sum + item.coefficient * item.sign * item.term.a, 0);
      const leftConst = signedTerms.reduce((sum, item) => sum + item.coefficient * item.sign * item.term.b, 0);
      const diffCoef = leftCoef - rhsCoef;
      const diffConst = leftConst - rhsConst;
      const raw = solutionForLinearInequality(diffCoef, diffConst, relation);
      const local = normalizeIntervals(raw.map((interval) => intersectIntervals(base, interval)).filter(Boolean));
      pieces.push(...local);
      const reduced = compactLinearLatex(leftCoef, leftConst);
      const diffLatex = compactLinearLatex(diffCoef, diffConst);
      caseSteps.push(`${intervalLabel(base)}:\\quad ${substituted}\\;${relation}\\;${rhsLatex}\\Rightarrow ${expanded}\\;${relation}\\;${rhsLatex}\\Rightarrow ${reduced}\\;${relation}\\;${rhsLatex}\\Rightarrow ${diffLatex}\\;${relation}\\;0\\Rightarrow x\\in ${intervalsLatex(local)}`);
    });
    const answer = normalizeIntervals(pieces);
    if (!answer.length || answer.length > 4 || isAllRealsAnswer(answer)) continue;
    const nice = answer.every(intervalLooksOk);
    if (!nice) continue;
    return {
      type: "AdvancedAbsoluteInequalityTask",
      data: {
        id: `double-ineq-${weighted ? "weighted" : "plain"}-${withLinearRight ? "linear" : "const"}-${leftRoot}-${rightRoot}-${leftScale}-${rightScale}-${absoluteCoefficients.join("_")}-${relation}-${rhsCoef}-${rhsConst}`,
        family: "double",
        weighted,
        absoluteCoefficients,
        outsideLinearCoefficient: rhsCoef,
        equation: `${scaledAbsoluteLatex(absoluteCoefficients[0], leftTerm.latex)}${scaledAbsoluteLatex(absoluteCoefficients[1], rightTerm.latex, 1)}\\;${relation}\\;${rhsLatex}`,
        critical: [leftRoot, rightRoot],
        splitLatex: `${leftTerm.latex},\\;${rightTerm.latex}`,
        intervals: baseIntervals.map(intervalLabel),
        answer,
        steps: [
          `Punkty krytyczne: x=${numberLatex(leftRoot)}\\;\\text{oraz}\\;x=${numberLatex(rightRoot)}`,
          ...caseSteps,
        ],
      },
      answer,
      maxPoints: 1,
    };
  }
  if (!fallback) {
    return buildDoubleTask({
      withLinearRight,
      scaled,
      weighted,
      relationDirection,
      fallback: true,
    });
  }
  return buildDoubleStaticFallback(relationDirection, { withLinearRight, weighted });
}

export function generateTask(options = {}) {
  if (options.kind === "nested-simple") return buildNestedTask({ linear: false, relationDirection: options.relationDirection });
  if (options.kind === "nested-linear") return buildNestedTask({ linear: true, relationDirection: options.relationDirection });
  if (options.kind === "single-linear") return buildSingleLinearTask(options);
  return buildDoubleTask(options);
}

export function renderTask({ data }) {
  return (
    <div>
      <p>{"Rozwi\u0105\u017c algebraicznie nier\u00f3wno\u015b\u0107:"}</p>
      <div style={{ textAlign: "center", margin: "10px 0 14px" }}><BlockMath math={data.equation} /></div>
    </div>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  const value = typeof userInput === "string" ? userInput : userInput?.S || "";
  return (
    <div>
      <IntervalInput
        value={value}
        onChange={(val) => onChange({ ...userInput, S: val })}
        disabled={disabled}
        asString
        previewWithX
        prefixAbove
        placeholder="np. (-inf; 2] U [5; inf), [-3; 1], {2}, R albo \u2205"
      />
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : userInput?.S?.raw || userInput?.S || "";
  const parsed = parseIntervalInput(raw);
  return sameIntervals(task.data.answer, parsed);
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${intervalsLatex(data.answer)}`} />;
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : userInput?.S?.raw || userInput?.S || "";
  return raw ? <InlineMath math={intervalToLatex(raw, { withX: true })} /> : <i>(brak odpowiedzi)</i>;
}

function splitCaseStep(step) {
  const match = String(step).match(/^(.+?):\\quad\s*(.+)$/);
  if (!match) return null;
  return { interval: match[1], parts: match[2].split(/\\Rightarrow/).map((item) => item.trim()).filter(Boolean) };
}

function uniqueSorted(values) {
  return Array.from(new Set(values.map((value) => Number(value.toFixed(10))))).sort((a, b) => a - b);
}
function parseLinearExpression(latex) {
  const normalized = String(latex || "")
    .replace(/\\;/g, "")
    .replace(/\s+/g, "")
    .replace(/\\left\|/g, "")
    .replace(/\\right\|/g, "");
  const standard = normalized.match(/^([+-]?\d*)x([+-]\d+)?$/);
  if (standard) {
    const aRaw = standard[1];
    const a = aRaw === "" || aRaw === "+" ? 1 : aRaw === "-" ? -1 : Number(aRaw);
    const b = standard[2] ? Number(standard[2]) : 0;
    if (Number.isFinite(a) && a !== 0 && Number.isFinite(b)) return { a, b, root: -b / a };
  }
  const reversed = normalized.match(/^([+-]?\d+)([+-])(\d*)x$/);
  if (reversed) {
    const b = Number(reversed[1]);
    const coefficient = reversed[3] === "" ? 1 : Number(reversed[3]);
    const a = reversed[2] === "-" ? -coefficient : coefficient;
    if (Number.isFinite(a) && a !== 0) return { a, b, root: -b / a };
  }
  return null;
}

function plainNumberLabel(value) {
  return numberRaw(value);
}
function splitSignExpressions(data) {
  const expressions = String(data.splitLatex || "")
    .split(/,|\\;/)
    .map((item) => item.trim())
    .filter(Boolean);
  return expressions.map((expression, index) => {
    const parsed = parseLinearExpression(expression);
    const root = Number.isFinite(parsed?.root) ? parsed.root : data.critical?.[index];
    const a = Number.isFinite(parsed?.a) ? parsed.a : 1;
    return {
      expression,
      root,
      increasing: a > 0,
      leftSign: a > 0 ? "-" : "+",
      rightSign: a > 0 ? "+" : "-",
    };
  }).filter((item) => Number.isFinite(item.root));
}

function CombinedSignAxis({ items }) {
  const roots = uniqueSorted(items.map((item) => item.root));
  if (roots.length < 2) return null;
  const width = 680;
  const height = 218;
  const left = 68;
  const right = width - 42;
  const axisY = 108;
  const rootX = (root) => {
    const index = roots.findIndex((value) => Math.abs(value - root) < TOL);
    return left + ((index + 1) * (right - left)) / (roots.length + 1);
  };
  const segmentXs = [
    left + 54,
    ...roots.slice(0, -1).map((root, index) => (rootX(root) + rootX(roots[index + 1])) / 2),
    right - 64,
  ];
  const segmentProbe = (index) => (index === 0
    ? roots[0] - 1
    : index === roots.length
      ? roots[roots.length - 1] + 1
      : (roots[index - 1] + roots[index]) / 2);
  const signFor = (item, index) => {
    const probe = segmentProbe(index);
    return item.increasing ? (probe < item.root ? "-" : "+") : (probe < item.root ? "+" : "-");
  };
  const slopeMagnitude = (() => {
    const pattern = items.map((item) => (item.increasing ? "u" : "d")).join("");
    if (pattern === "ud" || pattern === "du") return 0.34;
    return 0.28;
  })();
  const lineY = (item, x) => {
    const slope = item.increasing ? -slopeMagnitude : slopeMagnitude;
    return axisY + slope * (x - rootX(item.root));
  };
  const badgeOffset = (item, row, index, sign) => {
    const sameSignRows = items
      .map((entry, entryRow) => ({ entry, entryRow, sign: signFor(entry, index) }))
      .filter((entry) => entry.sign === sign);
    if (sameSignRows.length < 2) return 0;
    const sameDirection = sameSignRows.every((entry) => entry.entry.increasing === item.increasing);
    if (!sameDirection) return item.increasing ? 34 : -34;
    const ordinal = Math.max(0, sameSignRows.findIndex((entry) => entry.entryRow === row));
    return ordinal === 0 ? -30 : 30;
  };
  const lineClearance = (x, y) => Math.min(...items.map((entry) => Math.abs(y - lineY(entry, x))));
  const pickBadgeY = (sign, x, item) => {
    const lineAtBadge = lineY(item, x);
    const pocketY = axisY + (lineAtBadge - axisY) * 0.5;
    const upper = [axisY - 46, axisY - 66, axisY - 84];
    const lower = [axisY + 46, axisY + 66, axisY + 84];
    const pocketIsOnSignSide = sign === "+" ? pocketY < axisY : pocketY > axisY;
    const pocketFirst = Math.abs(lineAtBadge - axisY) >= 52 && pocketIsOnSignSide ? [pocketY] : [];
    const fallback = sign === "+" ? lower : upper;
    const candidates = sign === "+"
      ? [...pocketFirst, ...upper, ...fallback]
      : [...pocketFirst, ...lower, ...fallback];
    let best = candidates[0];
    let bestScore = Infinity;
    candidates.forEach((candidateY, index) => {
      const y = Math.max(30, Math.min(height - 30, candidateY));
      const clearance = lineClearance(x, y);
      const axisClearance = Math.abs(y - axisY);
      const collisionPenalty = clearance < 24 ? (24 - clearance) * 120 : 0;
      const axisPenalty = axisClearance < 26 ? (26 - axisClearance) * 40 : 0;
      const preferencePenalty = index * 6;
      const score = collisionPenalty + axisPenalty + preferencePenalty;
      if (score < bestScore) {
        bestScore = score;
        best = y;
      }
    });
    return best;
  };
  const badgePosition = (item, row, index, x) => {
    const sign = signFor(item, index);
    const xShift = badgeOffset(item, row, index, sign);
    const badgeX = x + xShift;
    return {
      x: badgeX,
      y: pickBadgeY(sign, badgeX, item),
      sign,
    };
  };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center", marginBottom: 8 }}>
        {items.map((item) => (
          <InlineMath key={item.expression} math={`${item.expression}=0\\iff x=${numberLatex(item.root, { mixed: true })}`} />
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Wspolny szkic znakow wyrazen liniowych">
        <line x1={left} y1={axisY} x2={right} y2={axisY} stroke="currentColor" strokeWidth="2" />
        <polygon points={`${right},${axisY} ${right - 12},${axisY - 7} ${right - 12},${axisY + 7}`} fill="currentColor" />
        {roots.map((root) => {
          const x = rootX(root);
          return (
            <g key={`root-${root}`}>
              <line x1={x} y1={axisY - 7} x2={x} y2={axisY + 7} stroke="currentColor" strokeWidth="2" />
              <text x={x} y={axisY + 30} textAnchor="middle" fontSize="19" fontFamily="KaTeX_Main, STIXGeneral, Times New Roman, serif" fill="currentColor">{plainNumberLabel(root)}</text>
            </g>
          );
        })}
        {items.map((item, row) => {
          const x0 = rootX(item.root);
          const lineLeft = left + 30;
          const lineRight = right - 30;
          const color = row === 0 ? "var(--color-accent, #f59e0b)" : "#8b5cf6";
          return (
            <g key={item.expression}>
              <line
                x1={lineLeft}
                y1={lineY(item, lineLeft)}
                x2={lineRight}
                y2={lineY(item, lineRight)}
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx={x0} cy={axisY} r="4" fill={color} />
              {segmentXs.map((x, index) => {
                const badge = badgePosition(item, row, index, x);
                return (
                  <g key={`${item.expression}-${index}`}>
                    <circle cx={badge.x} cy={badge.y} r="13" fill="rgba(0,0,0,.18)" stroke={color} strokeWidth="2" />
                    <text x={badge.x} y={badge.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="20" fontWeight="700" fontFamily="KaTeX_Main, STIXGeneral, Times New Roman, serif" fill={color}>{badge.sign}</text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <p style={{ margin: "0 0 2px", fontSize: "0.95rem", textAlign: "center", opacity: 0.9 }}>
        {"Miejsca zerowe wyra\u017ce\u0144 pod modu\u0142ami dziel\u0105 o\u015b na przedzia\u0142y. Plus czytamy nad osi\u0105, minus pod osi\u0105."}
      </p>
    </div>
  );
}


function renderIntervalChips(data) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
      {(data.intervals || []).map((interval) => (
        <span key={interval} style={{ padding: "3px 8px", border: "1px solid rgba(255,255,255,.22)", borderRadius: 999 }}><InlineMath math={interval} /></span>
      ))}
    </div>
  );
}

function renderCriticalSketch(data) {
  const items = splitSignExpressions(data);
  if (items.length > 1) {
    return (
      <div style={{ margin: "12px 0", padding: "12px 14px", border: "1px solid rgba(255,255,255,.24)", borderRadius: 8, maxWidth: 760 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{"Znaki wyra\u017ce\u0144 pod warto\u015bci\u0105 bezwzgl\u0119dn\u0105"}</div>
        <CombinedSignAxis items={items} />
        {renderIntervalChips(data)}
      </div>
    );
  }

  const points = Array.from(new Set((data.critical || []).filter(Number.isFinite).map((x) => Number(x.toFixed(10))))).sort((a, b) => a - b);
  const width = 620;
  const height = 104;
  const left = 44;
  const right = width - 40;
  const axisY = 42;
  const xFor = (index) => left + ((index + 1) * (right - left)) / (points.length + 1);
  return (
    <div style={{ margin: "12px 0", padding: "10px 12px", border: "1px solid rgba(255,255,255,.22)", borderRadius: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{"Punkty krytyczne dziel\u0105 o\u015b na przedzia\u0142y"}</div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Podzial osi na przedzialy">
        <line x1={left} y1={axisY} x2={right} y2={axisY} stroke="currentColor" strokeWidth="2" />
        <polygon points={`${right},${axisY} ${right - 12},${axisY - 7} ${right - 12},${axisY + 7}`} fill="currentColor" />
        {points.map((point, index) => {
          const x = xFor(index);
          return (
            <g key={`${point}-${index}`}>
              <line x1={x} y1={axisY - 18} x2={x} y2={axisY + 18} stroke="var(--color-accent, #ff9900)" strokeWidth="3" />
              <text x={x} y={axisY + 38} textAnchor="middle" fontSize="18" fontFamily="KaTeX_Main, STIXGeneral, Times New Roman, serif" fill="currentColor">{numberLatex(point).replace(/\\dfrac\{([^}]+)\}\{([^}]+)\}/, "$1/$2")}</text>
            </g>
          );
        })}
      </svg>
      {renderIntervalChips(data)}
    </div>
  );
}
function flipRelation(relation) {
  if (relation === "<") return ">";
  if (relation === ">") return "<";
  if (relation === "\\le") return "\\ge";
  return "\\le";
}

function linearInequalityLines(linearLatex, relation, rhs) {
  const parsed = parseLinearExpression(linearLatex);
  if (!parsed) return [`${linearLatex}\\;${relation}\\;${numberLatex(rhs, { mixed: true })}`];
  const lines = [`${linearLatex}\\;${relation}\\;${numberLatex(rhs, { mixed: true })}`];
  const afterConst = rhs - parsed.b;
  if (parsed.b !== 0) lines.push(`${compactLinearLatex(parsed.a, 0)}\\;${relation}\\;${numberLatex(afterConst, { mixed: true })}`);
  const finalRelation = parsed.a < 0 ? flipRelation(relation) : relation;
  const value = afterConst / parsed.a;
  lines.push(`x\\;${finalRelation}\\;${numberLatex(value, { mixed: true })}`);
  return Array.from(new Set(lines));
}

function renderLinearBranch(label, linearLatex, relation, rhs) {
  return (
    <div style={{ marginTop: 10 }}>
      <p><strong>{label}</strong></p>
      <div style={{ textAlign: "center" }}>
        {linearInequalityLines(linearLatex, relation, rhs).map((line, index) => <BlockMath key={`${label}-${index}`} math={line} />)}
      </div>
    </div>
  );
}

function renderNestedModuleBreakdown(data) {
  const linear = data.linearLatex;
  const high = data.center + data.delta;
  const low = data.center - data.delta;
  const weak = data.relation === "\\le" || data.relation === "\\ge";
  const isUpper = data.relation === "<" || data.relation === "\\le";
  const upperRel = weak ? "\\le" : "<";
  const lowerRel = weak ? "\\ge" : ">";
  const oppositeLowerRel = weak ? "\\le" : "<";
  const innerUpperRel = weak ? "\\le" : "<";
  const highLatex = numberLatex(high, { mixed: true });
  const lowLatex = numberLatex(low, { mixed: true });

  if (isUpper) {
    return (
      <div>
        <p>{"Dla znaku mniejszo\u015bci rozwi\u0105zanie jest cz\u0119\u015bci\u0105 wsp\u00f3ln\u0105 warunk\u00f3w: modu\u0142 ma by\u0107 nie wi\u0119kszy od g\u00f3rnej granicy i, je\u015bli dolna granica jest dodatnia, nie mniejszy od dolnej granicy."}</p>
        <p><strong>{"Warunek od g\u00f3ry"}</strong>{" daje ograniczenie obustronne:"}</p>
        <div style={{ textAlign: "center" }}>
          <BlockMath math={`\\left|${linear}\\right|\\;${upperRel}\\;${highLatex}`} />
          <BlockMath math={`-${highLatex}\\;${upperRel}\\;${linear}\\;${upperRel}\\;${highLatex}`} />
        </div>
        {renderLinearBranch("Lewa nier\u00f3wno\u015b\u0107 z ograniczenia od g\u00f3ry:", linear, lowerRel, -high)}
        {renderLinearBranch("Prawa nier\u00f3wno\u015b\u0107 z ograniczenia od g\u00f3ry:", linear, upperRel, high)}
        {low > 0 ? (
          <>
            <p><strong>{"Warunek od do\u0142u"}</strong>{" rozbijamy na sum\u0119 dw\u00f3ch nier\u00f3wno\u015bci:"}</p>
            <div style={{ textAlign: "center" }}>
              <BlockMath math={`\\left|${linear}\\right|\\;${lowerRel}\\;${lowLatex}`} />
              <BlockMath math={`${linear}\\;${lowerRel}\\;${lowLatex}\\quad\\text{lub}\\quad ${linear}\\;${oppositeLowerRel}\\;-${lowLatex}`} />
            </div>
            {renderLinearBranch("Pierwsza nier\u00f3wno\u015b\u0107 z warunku od do\u0142u:", linear, lowerRel, low)}
            {renderLinearBranch("Druga nier\u00f3wno\u015b\u0107 z warunku od do\u0142u:", linear, oppositeLowerRel, -low)}
            <p>{"Na ko\u0144cu bierzemy cz\u0119\u015b\u0107 wsp\u00f3ln\u0105: warunek od g\u00f3ry musi zachodzi\u0107 jednocze\u015bnie z warunkiem od do\u0142u."}</p>
          </>
        ) : (
          <>
            <p><strong>{"Warunek od do\u0142u"}</strong>{" nie zaw\u0119\u017ca wyniku:"}</p>
            <div style={{ textAlign: "center" }}>
              <BlockMath math={`\\left|${linear}\\right|\\;${lowerRel}\\;${lowLatex}`} />
              <BlockMath math={`x\\in\\mathbb{R}`} />
            </div>
            <p>{"Warto\u015b\u0107 bezwzgl\u0119dna zawsze jest nieujemna, wi\u0119c jest wi\u0119ksza od liczby ujemnej. Przy cz\u0119\u015bci wsp\u00f3lnej ten warunek niczego ju\u017c nie zmienia."}</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <p>{"Dla znaku wi\u0119kszo\u015bci rozwi\u0105zanie jest sum\u0105 przypadk\u00f3w: wewn\u0119trzny modu\u0142 jest bardzo ma\u0142y albo bardzo du\u017cy."}</p>
      {low >= 0 ? (
        <>
          <p><strong>{"Pierwszy przypadek"}</strong>{": wewn\u0119trzny modu\u0142 jest ma\u0142y."}</p>
          <div style={{ textAlign: "center" }}>
            <BlockMath math={`\\left|${linear}\\right|\\;${innerUpperRel}\\;${lowLatex}`} />
            <BlockMath math={`-${lowLatex}\\;${innerUpperRel}\\;${linear}\\;${innerUpperRel}\\;${lowLatex}`} />
          </div>
          {renderLinearBranch("Lewa nier\u00f3wno\u015b\u0107 pierwszego przypadku:", linear, lowerRel, -low)}
          {renderLinearBranch("Prawa nier\u00f3wno\u015b\u0107 pierwszego przypadku:", linear, innerUpperRel, low)}
        </>
      ) : (
        <p>{"Pierwszy przypadek odpada, bo wymaga\u0142by, aby modu\u0142 by\u0142 mniejszy od liczby ujemnej."}</p>
      )}
      <p><strong>{"Drugi przypadek"}</strong>{": wewn\u0119trzny modu\u0142 jest du\u017cy."}</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`\\left|${linear}\\right|\\;${lowerRel}\\;${highLatex}`} />
        <BlockMath math={`${linear}\\;${lowerRel}\\;${highLatex}\\quad\\text{lub}\\quad ${linear}\\;${oppositeLowerRel}\\;-${highLatex}`} />
      </div>
      {renderLinearBranch("Pierwsza nier\u00f3wno\u015b\u0107 drugiego przypadku:", linear, lowerRel, high)}
      {renderLinearBranch("Druga nier\u00f3wno\u015b\u0107 drugiego przypadku:", linear, oppositeLowerRel, -high)}
      <p>{"Na ko\u0144cu \u0142\u0105czymy otrzymane przypadki sum\u0105 zbior\u00f3w."}</p>
    </div>
  );
}
function renderNestedExplanation({ data }) {
  return (
    <div className="space-y-4">
      <p><strong>{"Krok 1:"}</strong>{" Zdejmujemy zewn\u0119trzn\u0105 warto\u015b\u0107 bezwzgl\u0119dn\u0105."}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={data.nested.outer} /></div>
      <p>{"Po dodaniu "}<InlineMath math={numberLatex(data.center)} />{" do ka\u017cdej strony dostajemy warunek dla wewn\u0119trznego modu\u0142u:"}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={data.nested.shifted} /></div>
      <p><strong>{"Krok 2:"}</strong>{" Rozpisujemy nier\u00f3wno\u015b\u0107 z wewn\u0119trznym modu\u0142em."}</p>
      {renderNestedModuleBreakdown(data)}
      <p><strong>{"Krok 3:"}</strong>{" Zapisujemy zbi\u00f3r rozwi\u0105za\u0144 po po\u0142\u0105czeniu otrzymanych warunk\u00f3w."}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={`x\\in ${intervalsLatex(data.answer)}`} /></div>
      <p><strong>{"Odpowied\u017a ko\u0144cowa:"}</strong></p>
      <div style={{ textAlign: "center" }}>{renderAnswer({ data })}</div>
    </div>
  );
}export function renderExplanation({ data }) {
  if (data.family === "nested") return renderNestedExplanation({ data });
  const caseSteps = data.steps.filter((step) => splitCaseStep(step));
  return (
    <div className="space-y-4">
      <p><strong>{"Krok 1:"}</strong>{" Wyznaczamy punkty krytyczne i dzielimy o\u015b na przedzia\u0142y."}</p>
      <div style={{ textAlign: "center" }}><BlockMath math={data.steps[0].replace(/^Punkty krytyczne:\s*/, "")} /></div>
      {renderCriticalSketch(data)}
      <p>{"O znaku decyduj\u0105 wyra\u017cenia: "}<InlineMath math={data.splitLatex} />{"."}</p>
      <p><strong>{"Krok 2:"}</strong>{" W ka\u017cdym przedziale opuszczamy modu\u0142y i rozwi\u0105zujemy zwyk\u0142\u0105 nier\u00f3wno\u015b\u0107 liniow\u0105."}</p>
      {caseSteps.map((step, index) => {
        const parsed = splitCaseStep(step);
        return (
          <div key={`${data.id}-${index}`} style={{ margin: "16px 0", paddingLeft: 12, borderLeft: "3px solid var(--color-accent, #f59e0b)" }}>
            <p><strong>{"Przedzia\u0142 "}{index + 1}{": "}</strong><InlineMath math={parsed.interval} /></p>
            <div style={{ textAlign: "center" }}>{parsed.parts.map((part, partIndex) => <BlockMath key={partIndex} math={part} />)}</div>
          </div>
        );
      })}
      <p><strong>{"Odpowied\u017a ko\u0144cowa:"}</strong></p>
      <div style={{ textAlign: "center" }}>{renderAnswer({ data })}</div>
    </div>
  );
}
